import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { AppLayout } from "../../components/layout/AppLayout";
import {
  TICKET_STATUS_LABELS,
  addTicketComment,
  changeTicketStatus,
  getTicketById,
  getTicketModules,
  isSupportAgent,
  listTickets,
} from "../../services/api/ticketsApi";

const PAGE_SIZE = 10;

/** Timestamps da API vêm em UTC sem "Z"; sem isso o browser interpreta como local (+3h no BR). */
function parseApiDate(value) {
  if (value == null || value === "") return null;
  if (value instanceof Date) return Number.isNaN(value.getTime()) ? null : value;
  if (typeof value === "number") {
    const d = new Date(value);
    return Number.isNaN(d.getTime()) ? null : d;
  }
  let processed = String(value).trim();
  if (!processed) return null;
  if (processed.includes(" ")) processed = processed.replace(" ", "T");
  const noTz =
    /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}(:\d{2}(?:\.\d+)?)?$/.test(processed) &&
    !processed.endsWith("Z") &&
    !/[+-]\d{2}:?\d{2}$/.test(processed);
  const d = new Date(noTz ? `${processed}Z` : processed);
  return Number.isNaN(d.getTime()) ? null : d;
}

function formatDate(value) {
  if (!value) return "—";
  try {
    const d = parseApiDate(value);
    if (!d) return String(value);
    return d.toLocaleString("pt-BR");
  } catch {
    return String(value);
  }
}

function StatusBadge({ status }) {
  return (
    <span className={`ticket-badge ticket-badge--${String(status || "").toLowerCase()}`}>
      {TICKET_STATUS_LABELS[status] || status}
    </span>
  );
}

function isImage(mime, nameOrUrl) {
  if (mime && String(mime).toLowerCase().startsWith("image/")) return true;
  return /\.(jpe?g|png|webp|gif|bmp|svg)$/i.test(nameOrUrl || "");
}

function isVideo(mime, nameOrUrl) {
  if (mime && String(mime).toLowerCase().startsWith("video/")) return true;
  return /\.(mp4|webm|mov|m4v)$/i.test(nameOrUrl || "");
}

function collectMediaItems(ticket) {
  const items = [];
  (ticket?.attachments || []).forEach((a) => {
    if (isImage(a.mimeType, a.fileName || a.publicUrl) || isVideo(a.mimeType, a.fileName || a.publicUrl)) {
      items.push({
        url: a.publicUrl,
        name: a.fileName,
        type: isVideo(a.mimeType, a.fileName || a.publicUrl) ? "video" : "image",
      });
    }
  });
  (ticket?.comments || []).forEach((c) => {
    (c.attachments || []).forEach((a) => {
      if (isImage(a.mimeType, a.fileName || a.publicUrl) || isVideo(a.mimeType, a.fileName || a.publicUrl)) {
        items.push({
          url: a.publicUrl,
          name: a.fileName,
          type: isVideo(a.mimeType, a.fileName || a.publicUrl) ? "video" : "image",
        });
      }
    });
  });
  return items;
}

function AttachmentChip({ url, name, mime, onOpen }) {
  if (isImage(mime, name || url) || isVideo(mime, name || url)) {
    return (
      <button
        type="button"
        className="ticket-attachment-thumb"
        onClick={(e) => {
          e.preventDefault();
          e.stopPropagation();
          onOpen?.({ url, name, mime });
        }}
        title={`Ampliar ${name}`}
      >
        {isImage(mime, name || url) ? (
          <img src={url} alt={name} />
        ) : (
          <span className="inline-link-button" style={{ margin: 0 }}>
            Vídeo: {name}
          </span>
        )}
      </button>
    );
  }
  return (
    <a href={url} target="_blank" rel="noreferrer" className="inline-link-button">
      Arquivo: {name}
    </a>
  );
}

function MediaLightbox({ items, index, onClose, onNavigate, readyAtRef }) {
  if (!items?.length || index == null) return null;
  const item = items[index];
  if (!item) return null;

  return createPortal(
    <div
      className="media-lightbox"
      role="dialog"
      aria-modal="true"
      onClick={() => {
        if (readyAtRef?.current && Date.now() < readyAtRef.current) return;
        onClose();
      }}
      onKeyDown={(e) => {
        if (e.key === "Escape") onClose();
        if (e.key === "ArrowLeft") onNavigate(-1);
        if (e.key === "ArrowRight") onNavigate(1);
      }}
      tabIndex={-1}
      ref={(el) => el?.focus()}
    >
      <button
        type="button"
        className="media-lightbox-close"
        onClick={(e) => {
          e.stopPropagation();
          onClose();
        }}
        aria-label="Fechar"
      >
        ×
      </button>
      {items.length > 1 && (
        <>
          <button
            type="button"
            className="media-lightbox-nav media-lightbox-nav--prev"
            onClick={(e) => {
              e.stopPropagation();
              onNavigate(-1);
            }}
            aria-label="Anterior"
          >
            ‹
          </button>
          <button
            type="button"
            className="media-lightbox-nav media-lightbox-nav--next"
            onClick={(e) => {
              e.stopPropagation();
              onNavigate(1);
            }}
            aria-label="Próximo"
          >
            ›
          </button>
          <div className="media-lightbox-counter">
            {index + 1} / {items.length}
          </div>
        </>
      )}
      <div onClick={(e) => e.stopPropagation()}>
        {item.type === "video" ? (
          <video src={item.url} controls autoPlay />
        ) : (
          <img src={item.url} alt={item.name || "Anexo"} />
        )}
      </div>
    </div>,
    document.body
  );
}

export function TicketsPage() {
  const [agentOk, setAgentOk] = useState(null);
  const [modules, setModules] = useState([]);
  const [tickets, setTickets] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [search, setSearch] = useState("");
  const [filterStatus, setFilterStatus] = useState("");
  const [filterModule, setFilterModule] = useState("");
  const [filterCompany, setFilterCompany] = useState("");
  const [showFilters, setShowFilters] = useState(false);
  const [selectedId, setSelectedId] = useState(null);
  const [detail, setDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [commentBody, setCommentBody] = useState("");
  const [commentFiles, setCommentFiles] = useState([]);
  const [commentDropActive, setCommentDropActive] = useState(false);
  const [sendingComment, setSendingComment] = useState(false);
  const [changingStatus, setChangingStatus] = useState(false);
  const [actionMsg, setActionMsg] = useState("");
  const [lightboxIndex, setLightboxIndex] = useState(null);
  const mediaLightboxReadyAtRef = useRef(0);

  const loadList = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const result = await listTickets({
        scope: "all",
        status: filterStatus || undefined,
        moduleCode: filterModule || undefined,
        companyId: filterCompany.trim() || undefined,
        page,
        size: PAGE_SIZE,
      });
      setTickets(Array.isArray(result?.content) ? result.content : []);
      setTotal(result?.totalElements ?? 0);
    } catch (e) {
      setError(e.message || "Erro ao carregar chamados.");
      setTickets([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  }, [filterStatus, filterModule, filterCompany, page]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [agent, mods] = await Promise.all([isSupportAgent(), getTicketModules()]);
        if (cancelled) return;
        setAgentOk(agent);
        setModules(mods);
        if (!agent) {
          setError(
            "Seu usuário não está marcado como agente de suporte. Configure SUPPORT_AGENT_UIDS ou is_support_agent no IAM."
          );
          setLoading(false);
        }
      } catch (e) {
        if (!cancelled) {
          setAgentOk(false);
          setError(e.message || "Falha ao verificar permissão de suporte.");
          setLoading(false);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (agentOk) loadList();
  }, [agentOk, loadList]);

  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  const activeFilterCount = useMemo(() => {
    let n = 0;
    if (filterStatus) n += 1;
    if (filterModule) n += 1;
    if (filterCompany.trim()) n += 1;
    return n;
  }, [filterStatus, filterModule, filterCompany]);

  const displayedTickets = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return tickets;
    return tickets.filter(
      (t) =>
        t.title?.toLowerCase().includes(q) ||
        t.displayNumber?.toLowerCase().includes(q) ||
        t.moduleLabel?.toLowerCase().includes(q) ||
        t.companyName?.toLowerCase().includes(q) ||
        t.companyId?.toLowerCase().includes(q) ||
        t.createdByName?.toLowerCase().includes(q)
    );
  }, [tickets, search]);

  const mediaItems = useMemo(() => collectMediaItems(detail), [detail]);

  const openDetail = async (id) => {
    setSelectedId(id);
    setDetail(null);
    setDetailLoading(true);
    setActionMsg("");
    setCommentBody("");
    setCommentFiles([]);
    setLightboxIndex(null);
    try {
      const t = await getTicketById(id);
      setDetail(t);
    } catch (e) {
      setError(e.message || "Erro ao abrir chamado.");
      setSelectedId(null);
    } finally {
      setDetailLoading(false);
    }
  };

  const handleStatus = async (status) => {
    if (!detail) return;
    setChangingStatus(true);
    setActionMsg("");
    try {
      const updated = await changeTicketStatus(detail.id, status);
      setDetail(updated);
      setActionMsg("Status atualizado.");
      loadList();
    } catch (e) {
      setActionMsg(e.message || "Erro ao alterar status.");
    } finally {
      setChangingStatus(false);
    }
  };

  const handleComment = async (e) => {
    e.preventDefault();
    if (!detail || !commentBody.trim()) return;
    setSendingComment(true);
    setActionMsg("");
    try {
      const updated = await addTicketComment(detail.id, commentBody.trim(), commentFiles);
      setDetail(updated);
      setCommentBody("");
      setCommentFiles([]);
      setActionMsg("Comentário enviado.");
      loadList();
    } catch (err) {
      setActionMsg(err.message || "Erro ao comentar.");
    } finally {
      setSendingComment(false);
    }
  };

  const onPickFiles = (fileList) => {
    if (!fileList) return;
    setCommentFiles((prev) => [...prev, ...Array.from(fileList)].slice(0, 5));
  };

  const openMedia = (att) => {
    const idx = mediaItems.findIndex((m) => m.url === att.url);
    mediaLightboxReadyAtRef.current = Date.now() + 350;
    setLightboxIndex(idx >= 0 ? idx : 0);
  };

  const statusSelectOptions = useMemo(() => {
    if (!detail) return [];
    const from = detail.status;
    const map = {
      OPEN: [
        { value: "IN_PROGRESS", label: "Iniciar atendimento" },
        { value: "CANCELLED", label: "Cancelar" },
        { value: "ARCHIVED", label: "Arquivar" },
      ],
      IN_PROGRESS: [
        { value: "WAITING_REQUESTER", label: "Aguardar solicitante" },
        { value: "RESOLVED", label: "Marcar resolvido" },
        { value: "CANCELLED", label: "Cancelar" },
        { value: "ARCHIVED", label: "Arquivar" },
      ],
      WAITING_REQUESTER: [
        { value: "IN_PROGRESS", label: "Retomar atendimento" },
        { value: "RESOLVED", label: "Marcar resolvido" },
        { value: "CANCELLED", label: "Cancelar" },
        { value: "ARCHIVED", label: "Arquivar" },
      ],
      RESOLVED: [
        { value: "CLOSED", label: "Fechar" },
        { value: "CANCELLED", label: "Cancelar" },
        { value: "ARCHIVED", label: "Arquivar" },
      ],
      CLOSED: [{ value: "ARCHIVED", label: "Arquivar" }],
      CANCELLED: [{ value: "ARCHIVED", label: "Arquivar" }],
      REOPENED: [
        { value: "IN_PROGRESS", label: "Iniciar atendimento" },
        { value: "CANCELLED", label: "Cancelar" },
        { value: "ARCHIVED", label: "Arquivar" },
      ],
      ARCHIVED: [],
    };
    return map[from] || [];
  }, [detail]);

  const timelineItems = useMemo(() => {
    if (!detail) return [];
    const events = (detail.events || []).map((ev) => ({
      kind: "event",
      id: `ev-${ev.id}`,
      at: ev.createdAt,
      ev,
    }));
    const comments = (detail.comments || []).map((c) => ({
      kind: "comment",
      id: `c-${c.id}`,
      at: c.createdAt,
      c,
    }));
    return [...events, ...comments].sort((a, b) => {
      const ta = a.at ? new Date(a.at).getTime() : 0;
      const tb = b.at ? new Date(b.at).getTime() : 0;
      return ta - tb;
    });
  }, [detail]);

  return (
    <AppLayout>
      <section className="tickets-page">
        {!selectedId && (
          <div className="page-toolbar">
            <div className="page-toolbar-top">
              <div>
                <h2 className="page-title">Chamados</h2>
                <p className="section-description">Inbox global — chamados de todas as empresas.</p>
              </div>
              <button type="button" className="secondary-button" onClick={loadList} disabled={loading}>
                Atualizar
              </button>
            </div>

            <div className="page-search-pill">
              <span className="page-search-icon" aria-hidden>
                ⌕
              </span>
              <input
                type="search"
                placeholder="Buscar por número, título, empresa…"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                aria-label="Buscar chamados"
              />
              <span className="page-search-divider" aria-hidden />
              <button
                type="button"
                className="page-filter-btn"
                onClick={() => setShowFilters((v) => !v)}
              >
                Filtros
                {activeFilterCount > 0 ? (
                  <span className="page-filter-count">{activeFilterCount}</span>
                ) : null}
              </button>
            </div>

            {showFilters && (
              <div className="page-filter-panel">
                <select
                  value={filterStatus}
                  onChange={(e) => {
                    setFilterStatus(e.target.value);
                    setPage(0);
                  }}
                  aria-label="Status"
                >
                  <option value="">Ativos (sem cancelados/arquivados)</option>
                  {Object.entries(TICKET_STATUS_LABELS).map(([k, v]) => (
                    <option key={k} value={k}>
                      {v}
                    </option>
                  ))}
                </select>
                <select
                  value={filterModule}
                  onChange={(e) => {
                    setFilterModule(e.target.value);
                    setPage(0);
                  }}
                  aria-label="Módulo"
                >
                  <option value="">Todos os módulos</option>
                  {modules.map((m) => (
                    <option key={m.code} value={m.code}>
                      {m.label}
                    </option>
                  ))}
                </select>
                <input
                  placeholder="Company ID (UUID)"
                  value={filterCompany}
                  onChange={(e) => {
                    setFilterCompany(e.target.value);
                    setPage(0);
                  }}
                />
                <button
                  type="button"
                  className="secondary-button"
                  onClick={() => {
                    setFilterStatus("");
                    setFilterModule("");
                    setFilterCompany("");
                    setPage(0);
                  }}
                >
                  Limpar
                </button>
              </div>
            )}

            {activeFilterCount > 0 && (
              <div className="page-chips">
                {filterStatus ? (
                  <span className="page-chip">
                    Status: {TICKET_STATUS_LABELS[filterStatus] || filterStatus}
                    <button
                      type="button"
                      aria-label="Remover filtro de status"
                      onClick={() => {
                        setFilterStatus("");
                        setPage(0);
                      }}
                    >
                      ×
                    </button>
                  </span>
                ) : null}
                {filterModule ? (
                  <span className="page-chip">
                    Módulo: {modules.find((m) => m.code === filterModule)?.label || filterModule}
                    <button
                      type="button"
                      aria-label="Remover filtro de módulo"
                      onClick={() => {
                        setFilterModule("");
                        setPage(0);
                      }}
                    >
                      ×
                    </button>
                  </span>
                ) : null}
                {filterCompany.trim() ? (
                  <span className="page-chip">
                    Empresa: {filterCompany.trim()}
                    <button
                      type="button"
                      aria-label="Remover filtro de empresa"
                      onClick={() => {
                        setFilterCompany("");
                        setPage(0);
                      }}
                    >
                      ×
                    </button>
                  </span>
                ) : null}
              </div>
            )}
          </div>
        )}

        {error && <div className="error-box">{error}</div>}
        {actionMsg && !error && (
          <div className={actionMsg.toLowerCase().includes("erro") ? "error-box" : "success-box"}>
            {actionMsg}
          </div>
        )}

        {agentOk === false ? null : selectedId ? (
          <div className="ticket-detail">
            <button
              type="button"
              className="secondary-button"
              onClick={() => {
                setSelectedId(null);
                setDetail(null);
                setActionMsg("");
                setLightboxIndex(null);
              }}
            >
              ← Voltar à lista
            </button>

            {detailLoading || !detail ? (
              <p className="muted-text">Carregando detalhe…</p>
            ) : (
              <>
                <div className="ticket-detail-header">
                  <div>
                    <div className="ticket-detail-meta">
                      <code>{detail.displayNumber}</code>
                      <StatusBadge status={detail.status} />
                    </div>
                    <h3 style={{ margin: "8px 0 4px" }}>{detail.title}</h3>
                    <p className="muted-text">
                      {detail.moduleLabel}
                      {detail.companyName ? ` · ${detail.companyName}` : ""}
                      {detail.createdByName ? ` · ${detail.createdByName}` : ""}
                    </p>
                    {detail.companyId ? (
                      <p className="muted-text" style={{ marginTop: 4, fontSize: 12 }}>
                        {detail.companyId}
                      </p>
                    ) : null}
                  </div>
                  <div className="ticket-detail-actions">
                    {statusSelectOptions.length > 0 ? (
                      <div className="ticket-status-actions">
                        <span className="muted-text">Alterar status</span>
                        <div className="ticket-status-actions-row">
                          {statusSelectOptions.map((o) => (
                            <button
                              key={o.value}
                              type="button"
                              className={
                                o.value === "CANCELLED" || o.value === "ARCHIVED"
                                  ? "secondary-button"
                                  : "primary-button"
                              }
                              disabled={changingStatus}
                              onClick={() => {
                                if (o.value === "ARCHIVED") {
                                  if (
                                    !window.confirm(
                                      "Arquivar este chamado? Ele sairá da lista ativa da empresa."
                                    )
                                  ) {
                                    return;
                                  }
                                }
                                handleStatus(o.value);
                              }}
                            >
                              {changingStatus ? "Salvando…" : o.label}
                            </button>
                          ))}
                        </div>
                      </div>
                    ) : detail.status === "CANCELLED" ? (
                      <p className="muted-text">Chamado cancelado — não pode ser reaberto.</p>
                    ) : detail.status === "ARCHIVED" ? (
                      <p className="muted-text">Chamado arquivado.</p>
                    ) : null}
                  </div>
                </div>

                <div className="ticket-description-box">
                  <h4>Descrição</h4>
                  <p style={{ whiteSpace: "pre-wrap", margin: 0 }}>{detail.description}</p>
                  {detail.attachments?.length > 0 && (
                    <div className="ticket-attachments">
                      {detail.attachments.map((a) => (
                        <AttachmentChip
                          key={a.id}
                          url={a.publicUrl}
                          name={a.fileName}
                          mime={a.mimeType}
                          onOpen={openMedia}
                        />
                      ))}
                    </div>
                  )}
                  <p className="muted-text" style={{ marginTop: 12, marginBottom: 0 }}>
                    Aberto em {formatDate(detail.createdAt)}
                  </p>
                </div>

                <div className="ticket-timeline-wrap">
                  <h4>Timeline</h4>
                  <div className="ticket-timeline">
                    {timelineItems.map((item) => {
                      if (item.kind === "event") {
                        const ev = item.ev;
                        let label = ev.type;
                        if (ev.type === "CREATED") label = "Chamado criado";
                        if (ev.type === "STATUS_CHANGED") {
                          label = `Status: ${
                            ev.fromStatus ? TICKET_STATUS_LABELS[ev.fromStatus] : "?"
                          } → ${ev.toStatus ? TICKET_STATUS_LABELS[ev.toStatus] : "?"}`;
                        }
                        if (ev.type === "CANCELLED") label = "Chamado cancelado pelo suporte";
                        if (ev.type === "ARCHIVED") label = "Chamado arquivado";
                        if (ev.type === "REOPENED") label = "Chamado reaberto";
                        return (
                          <div key={item.id} className="ticket-event">
                            {label}
                            <span className="muted-text"> · {formatDate(ev.createdAt)}</span>
                          </div>
                        );
                      }
                      const c = item.c;
                      return (
                        <div
                          key={item.id}
                          className={`ticket-comment ${c.fromSupport ? "ticket-comment--support" : ""}`}
                        >
                          <div className="ticket-comment-head">
                            <strong>
                              {c.authorName || c.authorId}
                              {c.fromSupport ? " · Suporte" : ""}
                            </strong>
                            <span className="muted-text">{formatDate(c.createdAt)}</span>
                          </div>
                          <p style={{ whiteSpace: "pre-wrap", margin: "6px 0 0" }}>{c.body}</p>
                          {c.attachments?.length > 0 && (
                            <div className="ticket-attachments">
                              {c.attachments.map((a) => (
                                <AttachmentChip
                                  key={a.id}
                                  url={a.publicUrl}
                                  name={a.fileName}
                                  mime={a.mimeType}
                                  onOpen={openMedia}
                                />
                              ))}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>

                {detail.status !== "CLOSED" &&
                  detail.status !== "CANCELLED" &&
                  detail.status !== "ARCHIVED" && (
                    <form
                      className={`ticket-comment-form${commentDropActive ? " drop-active" : ""}`}
                      onSubmit={handleComment}
                      onDragEnter={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        setCommentDropActive(true);
                      }}
                      onDragOver={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        setCommentDropActive(true);
                      }}
                      onDragLeave={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        setCommentDropActive(false);
                      }}
                      onDrop={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        setCommentDropActive(false);
                        onPickFiles(e.dataTransfer.files);
                      }}
                    >
                      <textarea
                        rows={3}
                        value={commentBody}
                        onChange={(ev) => setCommentBody(ev.target.value)}
                        placeholder="Escreva uma resposta… (arraste anexos aqui)"
                      />
                      <div className="ticket-comment-form-row">
                        <label className="secondary-button" style={{ display: "inline-block" }}>
                          Anexar
                          <input
                            type="file"
                            accept="image/jpeg,image/png,image/webp,video/mp4,video/webm"
                            multiple
                            hidden
                            onChange={(ev) => onPickFiles(ev.target.files)}
                          />
                        </label>
                        {commentFiles.map((f, i) => (
                          <span key={`${f.name}-${i}`} className="ticket-file-chip">
                            {f.name}
                            <button
                              type="button"
                              onClick={() => setCommentFiles((prev) => prev.filter((_, j) => j !== i))}
                            >
                              ×
                            </button>
                          </span>
                        ))}
                        <button
                          type="submit"
                          className="primary-button"
                          disabled={sendingComment || !commentBody.trim()}
                          style={{ marginLeft: "auto" }}
                        >
                          {sendingComment ? "Enviando…" : "Enviar"}
                        </button>
                      </div>
                    </form>
                  )}

                <MediaLightbox
                  items={mediaItems}
                  index={lightboxIndex}
                  readyAtRef={mediaLightboxReadyAtRef}
                  onClose={() => setLightboxIndex(null)}
                  onNavigate={(delta) => {
                    setLightboxIndex((prev) => {
                      if (prev == null || !mediaItems.length) return null;
                      const next = (prev + delta + mediaItems.length) % mediaItems.length;
                      return next;
                    });
                  }}
                />
              </>
            )}
          </div>
        ) : (
          <div className="crm-table-shell">
            <div className="crm-table-scroll">
              <table className="crm-table">
                <thead>
                  <tr>
                    <th>Número</th>
                    <th>Empresa</th>
                    <th>Título</th>
                    <th>Módulo</th>
                    <th>Status</th>
                    <th>Atualizado</th>
                  </tr>
                </thead>
                <tbody>
                  {loading ? (
                    <tr>
                      <td colSpan={6} className="td-empty">
                        Carregando chamados…
                      </td>
                    </tr>
                  ) : displayedTickets.length === 0 ? (
                    <tr>
                      <td colSpan={6} className="td-empty">
                        Nenhum chamado encontrado.
                      </td>
                    </tr>
                  ) : (
                    displayedTickets.map((t) => (
                      <tr
                        key={t.id}
                        className="ticket-row"
                        onClick={() => openDetail(t.id)}
                        onKeyDown={(e) => {
                          if (e.key === "Enter") openDetail(t.id);
                        }}
                        tabIndex={0}
                        role="button"
                      >
                        <td>
                          <code style={{ color: "var(--linqer-primary)", fontWeight: 600 }}>
                            {t.displayNumber}
                          </code>
                        </td>
                        <td>
                          <div>{t.companyName || "—"}</div>
                          <div className="table-sub muted-text">{t.companyId}</div>
                        </td>
                        <td>
                          <strong>{t.title}</strong>
                          {t.createdByName ? (
                            <div className="table-sub muted-text">{t.createdByName}</div>
                          ) : null}
                        </td>
                        <td>{t.moduleLabel}</td>
                        <td>
                          <StatusBadge status={t.status} />
                        </td>
                        <td>{formatDate(t.updatedAt)}</td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
            <div className="crm-table-pagination">
              <span>
                Página {page + 1} de {totalPages}
                {total > 0 ? ` · ${total} chamado${total === 1 ? "" : "s"}` : ""}
              </span>
              <div style={{ display: "flex", gap: 8 }}>
                <button type="button" disabled={page <= 0 || loading} onClick={() => setPage((p) => p - 1)}>
                  Anterior
                </button>
                <button
                  type="button"
                  disabled={page + 1 >= totalPages || loading}
                  onClick={() => setPage((p) => p + 1)}
                >
                  Próxima
                </button>
              </div>
            </div>
          </div>
        )}
      </section>
    </AppLayout>
  );
}
